import { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { Autocomplete } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { IAutocompleteOption } from 'core/types';
import { CorporateUserStatus } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';

interface IProps {
  onChange: (users: ICorporateUserAutocomplete[]) => void;
  value: ICorporateUserAutocomplete[];
}

export const UsersFilter = ({ onChange, value }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  const hasAllLocationsAccess =
    user?.companyAdmin || hasPermission(AccessPermissions.QUOTE_OMIT_DEFAULT_SALES_LOCATION);

  const canFilterByUsers = user?.branchManager || hasAllLocationsAccess;

  const requestPayload = useMemo(() => {
    const filters: Record<string, unknown> = {
      status: `"${CorporateUserStatus.ACTIVE}"`,
    };

    if (user?.branchManager && !hasAllLocationsAccess) {
      filters.addressId = user.location?.addressId;
    }

    return { filters };
  }, [user?.branchManager, hasAllLocationsAccess, user?.location?.addressId]);

  const handleChange = (_: SyntheticEvent, selectedUsers: IAutocompleteOption) => {
    onChange(selectedUsers as ICorporateUserAutocomplete[]);
  };

  if (!canFilterByUsers) {
    return null;
  }

  return (
    <Autocomplete
      name="corporateUsers"
      placeholder={t('placeholderSelectUsers')}
      getOptions={corporateUserService.getCorporateUsersAutocomplete}
      requestPayload={requestPayload}
      value={value}
      onChange={handleChange}
      optionKey="userId"
      autocompleteStyle={{ minWidth: 250, height: '100%' }}
      limitTags={2}
      multiple
    />
  );
};
